// Pseudo-step for the report page. The provider's `origin` holds the step to return to.
export const REPORT_STEP = "user-reported-error";
export const REPORT_TEXT_LIMIT = 600;
// Shortest text accepted after Something else, not counting spaces at the ends.
export const MIN_DETAILS_LENGTH = 10;

// The PostHog survey lists its choices in this order, so add new reasons last.
export const REASON_LABELS = {
  "credentials-correct": "My login details are correct",
  "document-supported": "My document is correct",
  "wait-too-long": "This took too long",
  other: "Something else",
} as const;

export type ReportReason = keyof typeof REASON_LABELS;

export const REASONS = Object.keys(REASON_LABELS) as ReportReason[];

type AreaConfig = {
  // Steps that offer the report link.
  steps: string[];
  // Choices shown for this area, in order.
  reasons: ReportReason[];
  // Separate visits to `steps` before the link shows. Defaults to 1.
  minVisits?: number;
};

// Keeps the area names as keys while typing every entry as AreaConfig.
const defineAreas = <T extends Record<string, AreaConfig>>(areas: T) =>
  areas as Record<keyof T, AreaConfig>;

// To add an area, add an entry here and render a ReportLink on its steps.
export const AREAS = defineAreas({
  login: {
    steps: ["max-login-attempts"],
    reasons: ["credentials-correct", "wait-too-long", "other"],
  },
  "document-upload": {
    steps: [
      "manual-document-error",
      "unsupported-document",
      "document-missing-fields",
      "document-type-not-supported",
      "text-validation-failed",
    ],
    reasons: ["document-supported", "wait-too-long", "other"],
    // Each failed attempt is a visit, and the link shows from the second.
    minVisits: 2,
  },
});

export type ReportingArea = keyof typeof AREAS;

export type Attempt = {
  integration?: string;
  carrierId?: string;
  loginPath?: "legacy" | "v123";
  loginErrorCode?: string;
  documentRef?: string;
  documentClassification?: string;
};

export type ReportInput = {
  submissionId: string;
  createdAt: string;
  area: ReportingArea;
  reason: ReportReason;
  details: string;
  step: string;
  attempt?: Attempt;
};

// Resolves once the destination accepts the report; rejects otherwise.
export type SubmitReport = (
  ignitionToken: string,
  input: ReportInput
) => Promise<void>;

export const areaForStep = (step: string) =>
  (Object.keys(AREAS) as ReportingArea[]).find((area) =>
    AREAS[area].steps.includes(step)
  );
